import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
} from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { GetEntitiesUpdatedGQL } from '@etoh/database/angular';
import {
  Agreements,
  BuySpecs,
  Companies,
  Items,
  MatchingItemsBuySpecs,
  SampleShipmentRequests,
  SelectOptions,
  StoreDataTypeKey,
} from '@etoh/database/core';
import { convertCurrencyToUSD } from 'apps/desktop/src/app/currenciesConversions/pure';
import {
  formatCurrency,
  formatNumber,
  formatPercent,
} from 'apps/desktop/src/app/locales/locales';
import { UserService } from 'apps/desktop/src/app/user.service';
import { endOfYear, startOfYear, sub } from 'date-fns';
import { flatten, isNumber, mean, sum } from 'lodash';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzSelectModule } from 'ng-zorro-antd/select';
import {
  combineLatest,
  map,
  Observable,
  of,
  startWith,
  switchMap,
  take,
} from 'rxjs';
import { AgreementsService } from '../../../agreements/agreements.service';
import { OfficesService } from '../../../offices/offices.service';
import { ListComponent } from '../../../shared/components/list/list.component';
import { StoreService } from '../../../store.service';
import { UsersService } from '../../../users.service';
import { StatsDisplayComponent } from '../../components/stats-display/stats-display.component';
import { Stat } from '../../dashboard.interfaces';

@Component({
  selector: 'etoh-metrics',
  standalone: true,
  imports: [
    CommonModule,
    StatsDisplayComponent,
    NzDatePickerModule,
    FormsModule,
    FlexLayoutModule,
    NzFormModule,
    NzSelectModule,
    ReactiveFormsModule,
    NzRadioModule,
  ],
  template: `
    @if (usersOptions$ | async; as usersOptions) {
    <section fxLayout="column" fxLayoutGap="8px">
      <div>
        <div fxLayout="row" fxLayoutGap="4px">
          @if ((isShowTypes$ | async)) {
          <div>
            <br />
            <br />

            <nz-radio-group [formControl]="type">
              @for (t of types$ | async; track $index) {
              <label nz-radio [nzValue]="t.value">{{ t.label }}</label>
              }
            </nz-radio-group>
          </div>
          }
          <span fxFlex></span>

          @if (type.value === 'office' || type.value === 'user') {
          <div>
            <form nz-form nzLayout="vertical">
              <nz-form-item>
                @if (type.value === 'office') {
                <nz-select
                  [formControl]="selectedOffice"
                  name="selectedOffice"
                  nzPlaceHolder="Select an office"
                  nzShowSearch
                >
                  @for (option of offices$ | async; track option.value) {
                  <nz-option
                    [nzLabel]="option.label"
                    [nzValue]="option.value"
                  ></nz-option>
                  }
                </nz-select>
                } @else if (type.value === 'user') {

                <nz-select
                  [formControl]="selectedUserId"
                  nzShowSearch
                  nzPlaceHolder="Select the user"
                  name="selectedUser"
                >
                  @for (option of usersOptions; track option.value) {
                  <nz-option
                    [nzLabel]="option.label"
                    [nzValue]="option.value"
                  ></nz-option>
                  }
                </nz-select>

                }
              </nz-form-item>
            </form>
          </div>
          }

          <div>
            <nz-range-picker
              [formControl]="dateFormControls"
              (ngModelChange)="onChange($event)"
            ></nz-range-picker>
          </div>
          <div>
            <nz-range-picker
              [formControl]="dateComparisonFormControls"
              (ngModelChange)="onChange($event)"
            ></nz-range-picker>
          </div>
        </div>
      </div>

      <div>
        @if (stats$ | async; as stats) {
        <etoh-stats-display [nzSpan]="6" [stats]="stats"></etoh-stats-display>
        } @else { Loading }
      </div>
    </section>
    } @else { Loading }
  `,
  styleUrl: './metrics.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MetricsComponent implements OnInit {
  type = new FormControl<'user' | 'office' | 'world'>('user');

  userService = inject(UserService);

  types$: Observable<SelectOptions[]> = this.userService.user$.pipe(
    map((user) => {
      let types = [
        {
          label: 'Broker',
          value: 'user',
        },
      ];

      if (user?.isManager) {
        types = [
          ...types,
          {
            label: 'Office',
            value: 'office',
          },
        ];
      }

      if (user?.isAdmin) {
        types = [
          ...types,
          {
            label: 'World',
            value: 'world',
          },
        ];
      }

      return types;
    })
  );

  isShowTypes$ = this.types$.pipe(map((types) => types.length > 1));

  officeService = inject(OfficesService);
  offices$ = this.officeService.getOfficesForOptions$();

  storeService = inject(StoreService);

  private usersService = inject(UsersService);
  selectedOffice = new FormControl(this.userService.userOfficeId$.value);

  selectedUserId = new FormControl(this.userService.userId$.value);

  usersOptions$: Observable<SelectOptions[]>;

  date = null;
  dateFormControls = new FormControl<null | [Date, Date]>([
    startOfYear(new Date()),
    new Date(),
  ]);
  dateComparisonFormControls = new FormControl<null | [Date, Date]>([
    sub(startOfYear(new Date()), { years: 1 }),
    sub(endOfYear(new Date()), { years: 1 }),
  ]);

  onChange(result: Date[]): void {
    console.log('onChange: ', result);
  }

  private modalService = inject(NzModalService);
  private getEntitiesUpdatedGQL = inject(GetEntitiesUpdatedGQL);

  private agreementService = inject(AgreementsService);

  stats$: Observable<Stat[]> = combineLatest([
    this.selectedUserId.valueChanges.pipe(startWith(this.selectedUserId.value)),
    this.selectedOffice.valueChanges.pipe(startWith(this.selectedOffice.value)),
    this.type.valueChanges.pipe(startWith(this.type.value)),
    this.dateFormControls.valueChanges.pipe(
      startWith(this.dateFormControls.value)
    ),
    this.dateComparisonFormControls.valueChanges.pipe(
      startWith(this.dateComparisonFormControls.value)
    ),
    combineLatest([
      this.dateFormControls.valueChanges.pipe(
        startWith(this.dateFormControls.value)
      ),
      this.dateComparisonFormControls.valueChanges.pipe(
        startWith(this.dateComparisonFormControls.value)
      ),
      this.selectedUserId.valueChanges.pipe(
        startWith(this.selectedUserId.value)
      ),
      this.selectedOffice.valueChanges.pipe(
        startWith(this.selectedOffice.value)
      ),
      this.type.valueChanges.pipe(startWith(this.type.value)),
      this.storeService.store.items.data$,
    ]).pipe(
      switchMap(
        ([
          dateRange,
          dateComparisonRange,
          selectedUserId,
          selectedOffice,
          type,
          items,
        ]) => {
          const userItems = (items as Items[]).filter((item) => {
            if (type === 'user') {
              return item.brokerId === selectedUserId;
            } else if (type === 'office') {
              return item.officeId === selectedOffice;
            } else {
              return true;
            }
          });

          return this.getDatasCreatedAndUpdated<Items>(
            'items',
            userItems,
            'createdAt',
            dateRange,
            dateComparisonRange
          );
        }
      )
    ),
    // agreements
    combineLatest([
      this.dateFormControls.valueChanges.pipe(
        startWith(this.dateFormControls.value)
      ),
      this.dateComparisonFormControls.valueChanges.pipe(
        startWith(this.dateComparisonFormControls.value)
      ),
      this.selectedUserId.valueChanges.pipe(
        startWith(this.selectedUserId.value)
      ),
      this.storeService.store.agreements.data$,
      this.selectedOffice.valueChanges.pipe(
        startWith(this.selectedOffice.value)
      ),
      this.type.valueChanges.pipe(startWith(this.type.value)),
    ]).pipe(
      switchMap(
        ([
          dateRange,
          dateComparisonRange,
          selectedUserId,
          agreements,
          selectedOffice,
          type,
        ]) => {
          const agreementsApproved = (agreements as Agreements[])
            .filter(
              (agreement) => agreement.contractStatus === 'approvedByParties'
            )
            .filter((agreement) => {
              if (type === 'user') {
                return (
                  agreement.buyerUserId === selectedUserId ||
                  agreement.sellerUserId === selectedUserId
                );
              } else if (type === 'office') {
                return (
                  agreement.buyerOfficeId === selectedOffice ||
                  agreement.sellerOfficeId === selectedOffice
                );
              } else {
                return true;
              }
            });

          return this.getDatasCreatedAndUpdated<Agreements>(
            'agreements',
            agreementsApproved,
            'startDate',
            dateRange,
            dateComparisonRange
          );
        }
      )
    ),

    // agreements
    combineLatest([
      this.dateFormControls.valueChanges.pipe(
        startWith(this.dateFormControls.value)
      ),
      this.dateComparisonFormControls.valueChanges.pipe(
        startWith(this.dateComparisonFormControls.value)
      ),
      this.selectedUserId.valueChanges.pipe(
        startWith(this.selectedUserId.value)
      ),
      this.storeService.store.agreements.data$,
      this.selectedOffice.valueChanges.pipe(
        startWith(this.selectedOffice.value)
      ),
      this.type.valueChanges.pipe(startWith(this.type.value)),
    ]).pipe(
      switchMap(
        ([
          dateRange,
          dateComparisonRange,
          selectedUserId,
          agreements,
          selectedOffice,
          type,
        ]) => {
          const agreementsPending = (agreements as Agreements[])
            .filter(
              (agreement) =>
                agreement.contractStatus === 'draft' ||
                agreement.contractStatus === 'pendingApproval' ||
                agreement.contractStatus === 'toBeSent'
            )
            .filter((agreement) => {
              if (type === 'user') {
                return (
                  agreement.buyerUserId === selectedUserId ||
                  agreement.sellerUserId === selectedUserId
                );
              } else if (type === 'office') {
                return (
                  agreement.buyerOfficeId === selectedOffice ||
                  agreement.sellerOfficeId === selectedOffice
                );
              } else {
                return true;
              }
            });

          return this.getDatasCreatedAndUpdated<Agreements>(
            'agreements',
            agreementsPending,
            'startDate',
            dateRange,
            dateComparisonRange
          );
        }
      )
    ),
    //
    combineLatest([
      this.dateFormControls.valueChanges.pipe(
        startWith(this.dateFormControls.value)
      ),
      this.dateComparisonFormControls.valueChanges.pipe(
        startWith(this.dateComparisonFormControls.value)
      ),
      this.selectedUserId.valueChanges.pipe(
        startWith(this.selectedUserId.value)
      ),
      this.storeService.store.buySpecs.data$,
      this.selectedOffice.valueChanges.pipe(
        startWith(this.selectedOffice.value)
      ),
      this.type.valueChanges.pipe(startWith(this.type.value)),
    ]).pipe(
      switchMap(
        ([
          dateRange,
          dateComparisonRange,
          selectedUserId,
          buySpecs,
          selectedOffice,
          type,
        ]) => {
          const userBuySpecs = (buySpecs as BuySpecs[]).filter((buySpec) => {
            if (type === 'user') {
              return buySpec.brokerId === selectedUserId;
            } else if (type === 'office') {
              return buySpec.officeId === selectedOffice;
            } else {
              return true;
            }
          });

          return this.getDatasCreatedAndUpdated<BuySpecs>(
            'buySpecs',
            userBuySpecs,
            'createdAt',
            dateRange,
            dateComparisonRange
          );
        }
      )
    ),
    combineLatest([
      this.storeService.store.companies.data$,
      this.selectedUserId.valueChanges.pipe(
        startWith(this.selectedUserId.value)
      ),
      this.type.valueChanges.pipe(startWith(this.type.value)),
      this.selectedOffice.valueChanges.pipe(
        startWith(this.selectedOffice.value)
      ),
    ]).pipe(
      map(([companies, selectedUserId, type, selectedOffice]) => {
        return (companies as Companies[]).filter((company) => {
          if (type === 'user') {
            return company.brokerId === selectedUserId;
          } else if (type === 'office') {
            return company.officeId === selectedOffice;
          } else {
            return true;
          }
        });
      })
    ),
    this.storeService.store.matchingItemsBuySpecs.data$,
    this.storeService.store.sampleShipmentRequests.data$,
    this.storeService.store.shipmentFulfillments.data$,
    this.storeService.store.items.data$,
    this.storeService.store.currenciesConversions.data$,
  ]).pipe(
    map(
      ([
        selectedUserId,
        selectedOfficeId,
        type,
        dateRange,
        dateComparisonRange,
        itemsProcessed,
        agreementsApprovedProcessed,
        agreementsPendingProcessed,
        buyspecsProcessed,
        companiesFiltered,
        matchingItemsBuySpecs,
        sampleShipmentRequests,
        shipmentFulfillments,
        items,
        currenciesConversions,
      ]) => {
        const itemsCreated = itemsProcessed[0].datas;
        const itemsComparison = itemsProcessed[1].datas;

        const companiesCreated = this.getRangeData<Companies>(
          dateRange,
          companiesFiltered as any,
          'createdAt'
        );
        const companiesComparison = this.getRangeData<Companies>(
          dateComparisonRange,
          companiesFiltered as any,
          'createdAt'
        );

        const matchingBuySpecsWithItems: MatchingItemsBuySpecs[] = (
          matchingItemsBuySpecs as MatchingItemsBuySpecs[]
        ).map((m) => {
          return {
            ...m,
            item: (items as Items[]).find((i) => i.id === m.itemId),
          } as any;
        });

        const userMatchingItemsBuySpecs = matchingBuySpecsWithItems.filter(
          (m) => {
            if (type === 'user') {
              return m?.item?.brokerId === selectedUserId;
            } else if (type === 'office') {
              return m?.item?.officeId === selectedOfficeId;
            } else {
              return true;
            }
          }
        );

        const matchingItemsBuySpecsCreated =
          this.getRangeData<MatchingItemsBuySpecs>(
            dateRange,
            userMatchingItemsBuySpecs as any,
            'createdAt'
          );
        const matchingItemsBuySpecsComparison =
          this.getRangeData<MatchingItemsBuySpecs>(
            dateComparisonRange,
            userMatchingItemsBuySpecs as any,
            'createdAt'
          );

        // ITEMS id in agreements
        const matchingItemsBuySpecsFromAgreementsCreatedId = flatten(
          agreementsApprovedProcessed[0].datas.map((a) => a.itemsId)
        );

        /*
        const matchingItemsBuySpecsFromAgreementsCreated = (
          matchingItemsBuySpecs as MatchingItemsBuySpecs[]
        ).filter((m) =>
          matchingItemsBuySpecsFromAgreementsCreatedId.includes(m.itemId)
        );
        const matchingItemsBuySpecsFromAgreementsComparisonId = flatten(
          agreementsApprovedProcessed[1].datas.map((a) => a.itemsId)
        );
        const matchingItemsBuySpecsFromAgreementsComparison = (
          matchingItemsBuySpecs as MatchingItemsBuySpecs[]
        ).filter((m) =>
          matchingItemsBuySpecsFromAgreementsComparisonId.includes(m.itemId)
        );
        */

        const inerbrokerAgreementsCreated =
          agreementsApprovedProcessed[0].datas.filter(
            (a) =>
              a.buyerUserId &&
              a.sellerUserId &&
              a.buyerUserId !== a.sellerUserId
          );
        const interbrokerAgreementsComparison =
          agreementsApprovedProcessed[1].datas.filter(
            (a) =>
              a.buyerUserId &&
              a.sellerUserId &&
              a.buyerUserId !== a.sellerUserId
          );

        const agreementsWithShipments =
          agreementsApprovedProcessed[0].datas.map((agreement) => {
            return {
              ...agreement,
              shipmentFulfillments: shipmentFulfillments.filter(
                (sf) => sf.agreementId === agreement.id
              ),
            };
          });

        const agreementsComparisonWithShipments =
          agreementsApprovedProcessed[1].datas.map((agreement) => {
            return {
              ...agreement,
              shipmentFulfillments: shipmentFulfillments.filter(
                (sf) => sf.agreementId === agreement.id
              ),
            };
          });

        const averageSamplePerBuyspec = this.getAverageSamplePerBuyspec(
          buyspecsProcessed[0].datas,
          sampleShipmentRequests
        );
        const averageSampleComparisonPerBuyspec =
          this.getAverageSamplePerBuyspec(
            buyspecsProcessed[1].datas,
            sampleShipmentRequests
          );

        const itemsStat = this.getSumEfficiencyFromNiumbers(
          itemsCreated.length,
          itemsComparison.length
        );

        const buyspecStat = this.getSumEfficiencyFromNiumbers(
          buyspecsProcessed[0].datas.length,
          buyspecsProcessed[1].datas.length
        );

        const agreeementsStat = this.getSumEfficiencyFromNiumbers(
          agreementsApprovedProcessed[0].datas.length,
          agreementsApprovedProcessed[1].datas.length
        );

        const agreementsStatMoney = sum(
          agreementsApprovedProcessed[0].datas.flatMap((agreement) => {
            if (type === 'user') {
              if (!selectedUserId) {
                return 0;
              }

              return convertCurrencyToUSD(
                currenciesConversions,
                agreement.createdAt,
                agreement.currency as any as string,
                this.agreementService.getCommissionFromAgreementForSpecificUser(
                  agreement,
                  selectedUserId
                )
              );
            } else if (type === 'office') {
              if (!selectedOfficeId) {
                return 0;
              }

              return convertCurrencyToUSD(
                currenciesConversions,
                agreement.createdAt,
                agreement.currency as any as string,
                this.agreementService.getCommissionFromAgreementForSpecificUser(
                  agreement,
                  undefined,
                  selectedOfficeId
                )
              );
            } else {
              const commission =
                this.agreementService.getSplitCommissionForBrokers(agreement);

              return convertCurrencyToUSD(
                currenciesConversions,
                agreement.createdAt,
                agreement.currency as any as string,
                commission.buyerBrokerCommission +
                  commission.sellerBrokerCommission
              );
            }
          })
        );

        agreementsApprovedProcessed[0].datas.map((a) => {
          const totalCost =
            this.agreementService.getCommissionFromAgreement(a) || 0;
          return totalCost;
        });

        const getMoney = <T extends BuySpecs | Items>(
          entities: T[],
          priceField: keyof T,
          volumeField: keyof T
        ) => {
          const costsItems = entities
            .filter((i) => i.currency)

            .map((i) =>
              convertCurrencyToUSD(
                currenciesConversions,
                i.createdAt,
                i.currency as string,
                ((i[priceField] as number) || 0) *
                  ((i[volumeField] as number) || 0) *
                  0.02
              )
            );

          return sum(costsItems);
        };

        const itemStatMoney = getMoney<Items>(
          itemsCreated,
          'localPrice',
          'quantity'
        );
        const buySpecsStatMoney = getMoney<BuySpecs>(
          buyspecsProcessed[0].datas,
          'askPrice',
          'quantityNeeded'
        );

        return [
          {
            title: 'Items created',
            ...this.getSumEfficiencyFromNiumbers(
              itemsCreated.length,
              itemsComparison.length
            ),

            sum:
              itemsStat.sum === '-'
                ? '-'
                : `${itemsStat.sum} | ${formatCurrency(
                    itemStatMoney,
                    undefined,
                    'USD',
                    '1.0-0'
                  )}`,
            efficiency: itemsStat.efficiency,

            tooltip:
              ' Number of items created by the selected broker during this period, compared to the previous period.',
            click: () => {
              this.getDialogOpenFunction('items', itemsCreated)();
            },
          },
          {
            title: 'BuySpecs created',

            sum:
              buyspecStat.sum === '-'
                ? '-'
                : `${buyspecStat.sum} | ${formatCurrency(
                    buySpecsStatMoney,
                    undefined,
                    'USD',
                    '1.0-0'
                  )}`,

            efficiency: buyspecStat.efficiency,
            tooltip:
              ' Number of BuySpecs created by the selected broker during this period, compared to the previous period.',
            click: () => {
              this.getDialogOpenFunction(
                'buySpecs',
                buyspecsProcessed[0].datas
              )();
            },
          },
          {
            title: 'Companies created',
            ...this.getSumEfficiencyFromNiumbers(
              companiesCreated.length,
              companiesComparison.length
            ),
            tooltip:
              'Number of companies created by the selected broker during this period, compared to the previous period.',

            click: () => {
              this.getDialogOpenFunction('companies', companiesCreated)();
            },
          },
          {
            title: 'Matches',
            ...this.getSumEfficiencyFromNiumbers(
              matchingItemsBuySpecsCreated.length,
              matchingItemsBuySpecsComparison.length
            ),
            tooltip:
              'Number of matches made between items and BuySpecs by the selected broker during this period, compared to the previous period.',
          },
          {
            title: 'S&P Agreements approved',
            tooltip:
              'Total number of S&P agreements (where the selected broker is involved) approved by all parties during this period, compared to the previous period.',

            sum:
              agreeementsStat.sum === '-'
                ? '-'
                : `${agreeementsStat.sum} | ${formatCurrency(
                    agreementsStatMoney,
                    undefined,
                    'USD',
                    '1.0-0'
                  )}`,
            efficiency: agreeementsStat.efficiency,
            click: () => {
              this.getDialogOpenFunction(
                'agreements',
                agreementsApprovedProcessed[0].datas
              )();
            },
          },
          {
            title: 'Pending S&P',
            tooltip:
              "Total number of unapproved S&P Agreements (with the status 'Draft', 'To Be Sent', or 'Pending approval') involving the selected broker, during this period, compared to the previous period.",
            ...this.getSumEfficiencyFromNiumbers(
              agreementsPendingProcessed[0].datas.length,
              agreementsPendingProcessed[1].datas.length
            ),
            click: () => {
              this.getDialogOpenFunction(
                'agreements',
                agreementsPendingProcessed[0].datas
              )();
            },
          },
          {
            title: 'Inter-Broker S&P Ratio',
            sum: this.getPercent(
              inerbrokerAgreementsCreated.length,
              agreementsApprovedProcessed[0].datas.length
            ),
            efficiency: this.getPercent(
              interbrokerAgreementsComparison.length,
              agreementsApprovedProcessed[1].datas.length
            ),
            tooltip:
              'Percentage of S&P agreements where the buyer’s broker and seller’s broker are different, during this period, compared to the previous period.',
            click: () => {
              this.getDialogOpenFunction(
                'agreements',
                inerbrokerAgreementsCreated
              )();
            },
          },
          {
            title: 'Match Conversion Rate',
            sum: this.getPercent(
              agreementsApprovedProcessed[0].datas.length,
              matchingItemsBuySpecsCreated.length
            ),
            efficiency: this.getPercent(
              agreementsApprovedProcessed[1].datas.length,
              matchingItemsBuySpecsComparison.length
            ),
            tooltip:
              'Percentage of Item-BuySpec matches that resulted in S&P agreements, during this period, compared to the previous period.',
          },
          {
            title: 'Average Samples/BuySpec',
            ...this.getSumEfficiencyFromNiumbers(
              averageSamplePerBuyspec,
              averageSampleComparisonPerBuyspec,
              '1.0-1'
            ),
            tooltip:
              'Average number of samples sent per BuySpec by the selected broker during this period, compared to the previous period.',
          },
          {
            title: 'Average Fulfillment',
            sum: this.getPercentOneValue(
              mean(
                agreementsWithShipments.map((a) =>
                  this.agreementService.getCompletionFromAgreement(a)
                )
              )
            ),
            efficiency: this.getPercentOneValue(
              mean(
                agreementsComparisonWithShipments.map((a) =>
                  this.agreementService.getCompletionFromAgreement(a)
                )
              )
            ),
            tooltip:
              'Average percentage of items successfully fulfilled in S&P agreements during this period, compared to the previous period.',

            click: () => {
              this.getDialogOpenFunction(
                'agreements',
                agreementsWithShipments
              )();
            },
          },

          {
            title: 'Updated Items',
            tooltip:
              'Percentage of items (created by the broker) updated during this period, compared to the previous period.',
            sum: this.getPercent(
              itemsProcessed[0].updated.length,
              itemsCreated.length
            ),
            efficiency: this.getPercent(
              itemsProcessed[1].updated.length,
              itemsComparison.length
            ),

            click: () => {
              this.getDialogOpenFunction('items', itemsProcessed[0].updated)();
            },
          },
          {
            title: 'Updated BuySpecs',
            sum: this.getPercent(
              buyspecsProcessed[0].updated.length,
              buyspecsProcessed[0].datas.length
            ),
            efficiency: this.getPercent(
              buyspecsProcessed[1].updated.length,
              buyspecsProcessed[1].datas.length
            ),
            tooltip:
              'Percentage of BuySpecs (created by the broker) updated during this period, compared to the previous period.',
            click: () => {
              this.getDialogOpenFunction(
                'buySpecs',
                buyspecsProcessed[0].updated
              )();
            },
          },
          {
            title: 'Updated S&P',
            sum: this.getPercent(
              agreementsApprovedProcessed[0].updated.length,
              agreementsApprovedProcessed[0].datas.length
            ),
            efficiency: this.getPercent(
              agreementsApprovedProcessed[1].updated.length,
              agreementsApprovedProcessed[1].datas.length
            ),
            tooltip:
              'Percentage of S&P agreements (where the selected broker is involved) updated during this period, compared to the previous period.',
            click: () => {
              this.getDialogOpenFunction(
                'agreements',
                agreementsApprovedProcessed[0].updated
              )();
            },
          },
        ];
      }
    )
  );

  ngOnInit(): void {
    this.usersOptions$ = this.usersService.getBrokersForOptions$();

    this.userService.user$
      .pipe(
        map((user) => !(user?.isManager || user?.isAdmin)),
        take(1)
      )
      .subscribe((res) => {
        if (res) {
          this.selectedUserId.disable();
        }
      });
  }

  private getDialogOpenFunction(
    dataType: StoreDataTypeKey,
    itemsCreated: any[]
  ) {
    return () => {
      console.log('click');
      const modal = this.modalService.create({
        nzTitle: `${dataType}`,
        nzContent: ListComponent,
        nzWidth: '80%',
      });
      const instance = modal.getContentComponent();
      const instanceRef = modal.getContentComponentRef();

      const eComponentParams = {
        dataType: dataType,
        data$: of(itemsCreated),
      };

      if (instanceRef) {
        for (const key in eComponentParams) {
          try {
            instanceRef.setInput(key, (eComponentParams as any)[key]);
          } catch (err) {
            console.log('err', err);
            window.alert(err);
          }
        }
      }
    };
  }

  private getUpdatedEntitiesId(
    table: StoreDataTypeKey,
    dateRange: [Date, Date] | null,
    itemsId: number[]
  ) {
    if (!dateRange?.[0] || !dateRange?.[1]) {
      return of([]);
    }
    return this.getEntitiesUpdatedGQL
      .fetch({
        startDate: dateRange?.[0]?.toISOString(),
        endDate: dateRange?.[1]?.toISOString(),
        table,
        objectsId: itemsId,
      })
      .pipe(
        map((res) => {
          return res.data.history.map((h) => h.objectId);
        })
      );
  }

  private getDatasCreatedAndUpdated<T>(
    table: StoreDataTypeKey,
    items: T[],
    keyDate: keyof T,
    dateRange: [Date, Date] | null,
    dateComparisonRange: [Date, Date] | null
  ) {
    const itemsCreated = this.getRangeData<T>(dateRange, items as any, keyDate);
    const itemsComparison = this.getRangeData<T>(
      dateComparisonRange,
      items as any,
      keyDate
    );

    return combineLatest([
      this.getUpdatedEntitiesId(
        table,
        dateRange,
        itemsCreated.map((i) => (i as any).id)
      ),
      this.getUpdatedEntitiesId(
        table,
        dateComparisonRange,
        itemsComparison.map((i) => (i as any).id)
      ),
    ]).pipe(
      map(([itemsCreatedUpdatedId, itemsUpdatedUpdatedId]) => {
        return [
          {
            dateRange: dateRange,
            datas: itemsCreated as T[],
            updated: itemsCreated.filter((i) =>
              itemsCreatedUpdatedId.includes((i as any).id)
            ) as T[],
          },
          {
            dateRange: dateComparisonRange,
            datas: itemsComparison as T[],
            updated: itemsComparison.filter((i) =>
              itemsUpdatedUpdatedId.includes((i as any).id)
            ) as T[],
          },
        ];
      })
    );
  }

  private getSumEfficiencyFromNiumbers(
    itemsCreated: number | undefined,
    itemsComparison: number | undefined,
    round: string | undefined = undefined
  ): {
    sum: string;
    efficiency: string;
  } {
    let efficiency = '';
    if (isNumber(itemsComparison) && isNumber(itemsCreated)) {
      if (itemsComparison === 0 || itemsCreated === 0) {
        efficiency = '';
      } else {
        efficiency = `| ${formatPercent(
          this.getPercentageDifference(itemsCreated, itemsComparison),
          undefined,
          '1.0-0'
        )}`;
      }
    }

    return {
      sum: itemsCreated ? formatNumber(itemsCreated, undefined, round) : '-',
      efficiency: !itemsComparison
        ? '-'
        : `vs ${formatNumber(itemsComparison, undefined, round)} ${efficiency}`,
    };
  }

  private getRangeData<T>(
    dateRange: null | [Date, Date],
    entities: T[],
    keyDate: keyof T
  ) {
    const dateStart = dateRange?.[0];
    const dateEnd = dateRange?.[1];

    if (!dateStart || !dateEnd) {
      return [];
    }

    const dateStartStr = dateStart.toISOString();
    const dateEndStr = dateEnd.toISOString();

    const filteredEntities = entities.filter(
      (entity) =>
        entity[keyDate] >= dateStartStr && entity[keyDate] <= dateEndStr
    );

    return filteredEntities;
  }

  private getPercentageDifference(num1: number, num2: number) {
    return (num1 - num2) / ((num1 + num2) / 2);
  }

  private getAverageSamplePerBuyspec(
    buySpecs: BuySpecs[],
    sampleShipmentRequests: SampleShipmentRequests[]
  ): number | undefined {
    const buyspecsCreatedWithSamples = buySpecs.map((buySpec) => {
      return {
        ...buySpec,
        sampleShipmentRequests: sampleShipmentRequests.filter((ssr) => {
          return ssr.buySpecId === buySpec.id;
        }),
      };
    });

    const x = buyspecsCreatedWithSamples.map((buySpec) =>
      sum(buySpec.sampleShipmentRequests.map((ssr) => ssr.numberOfSamples))
    );

    if (x.length === 0) {
      return undefined;
    }

    return mean(x);
  }

  private getPercentOneValue(value: number): string {
    if (!value) {
      return '-';
    }
    return formatPercent(value, undefined, '1.0-0');
  }

  private getPercent(itemsCreated: number, itemsComparison: number): string {
    if (itemsComparison === 0 || itemsCreated === 0) {
      return '-';
    }

    return formatPercent(itemsCreated / itemsComparison, undefined, '1.0-0');
  }
}
